import type { BookingSessionResponse } from '@massilia/queries'
import type { ReactNode } from 'react'

import { Separator, Skeleton } from '@massilia/ui'
import { Briefcase, Luggage } from 'lucide-react'

import { formatDuration } from '@/utils/format-duration'
import { formatPrice } from '@/utils/format-price'
import { PASSENGER_TYPE_LABELS } from '@/utils/passenger-labels'
import { JOURNEY_TYPE_LABELS } from '@/utils/trip-labels'

import { FlightSegmentCard, PassengerCard, TicketConditions } from './shared-trip-components'

const SHOW_RULE_DEBUG =
  import.meta.env.DEV || import.meta.env.VITE_SHOW_CONDITIONS_RULE_DEBUG === 'true'

// ── Props ──

interface BookingSidebarProps {
  /** Contact info to display (checkout only) */
  contactInfo?: {
    email: string
    phone?: string
  }
  /** When true, only the insurance price + total show a skeleton (rest of the recap stays visible) */
  insurancePriceLoading?: boolean
  isLoading?: boolean
  /** Override insurance price (used on assurances page where selection is dynamic) */
  overrideInsurancePrice?: number
  /** Promo code discount amount in EUR for total calculation */
  promoCodeDiscount?: null | number
  /** Promo code slot rendered between assurance line and total (checkout only) */
  promoCodeSlot?: ReactNode
  session?: BookingSessionResponse
}

// ── Component ──

export function BookingSidebar({
  contactInfo,
  insurancePriceLoading,
  isLoading,
  overrideInsurancePrice,
  promoCodeDiscount,
  promoCodeSlot,
  session,
}: BookingSidebarProps) {
  if (isLoading) {
    return (
      <div className="border-gray-light-200 bg-gray-light-50 flex flex-col gap-3 rounded-xl border px-6 py-8">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-4 w-56" />
        <Separator />
        <Skeleton className="h-20 w-full" />
        <Separator />
        <Skeleton className="h-20 w-full" />
        <Separator />
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-16 w-full" />
      </div>
    )
  }

  const priceData = session?.priceData
  const passengers = session?.passengers ?? []
  const insurance = session?.insurance
  const withInsurance = insurance?.withInsurance ?? false

  // Price computation
  const flightPriceCents = session?.totalFlightPriceCents ?? 0
  const flightPrice = flightPriceCents / 100

  // Insurance: use override if provided, otherwise use session insurance
  const insurancePrice =
    overrideInsurancePrice !== undefined
      ? overrideInsurancePrice
      : withInsurance
        ? (insurance?.insurancePriceCents ?? 0) / 100
        : 0
  const totalPrice = flightPrice + insurancePrice
  const effectiveTotal = Math.max(0, totalPrice - (promoCodeDiscount ?? 0))

  return (
    <div className="border-gray-light-200 bg-gray-light-50 sticky top-4 flex flex-col gap-3 rounded-xl border px-6 py-8">
      {/* Title */}
      <h3 className="text-gray-light-900 text-lg font-semibold">Détail du tarif</h3>

      {/* Vol — prix par type passager */}
      <div className="flex flex-col gap-1">
        <p className="text-gray-light-900 text-sm font-semibold">Vol</p>
        {priceData?.passengers.map((group, i) => (
          <div className="flex items-center justify-between" key={i}>
            <p className="text-gray-light-900 text-sm font-medium">
              {PASSENGER_TYPE_LABELS[group.type as keyof typeof PASSENGER_TYPE_LABELS] ??
                group.type}{' '}
              ({group.count})
            </p>
            <p className="text-gray-light-900 text-sm font-medium">
              {formatPrice(parseFloat(group.unitPrice))}
            </p>
          </div>
        ))}
        {/* Fallback if no per-type data but we have a total flight price */}
        {!priceData?.passengers.length && flightPrice > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-gray-light-900 text-sm font-medium">Sous total</p>
            <p className="text-gray-light-900 text-sm font-medium">{formatPrice(flightPrice)}</p>
          </div>
        )}
      </div>

      {/* Assurance */}
      {(insurancePrice > 0 || insurancePriceLoading) && (
        <div className="flex items-center justify-between">
          <p className="text-gray-light-900 text-sm font-medium">Assurance vol</p>
          {insurancePriceLoading ? (
            <Skeleton className="h-4 w-16" />
          ) : (
            <p className="text-gray-light-900 text-sm font-medium">{formatPrice(insurancePrice)}</p>
          )}
        </div>
      )}

      {/* Promo code slot (checkout only) */}
      {promoCodeSlot}

      <div className="bg-gray-light-100 h-px w-full" />

      {/* Total */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <span className="text-gray-light-900 text-lg font-semibold">Total</span>
          {insurancePriceLoading ? (
            <Skeleton className="h-6 w-24" />
          ) : (
            <span className="text-gray-light-900 text-lg font-semibold">
              {formatPrice(effectiveTotal)}
            </span>
          )}
        </div>
        <p className="text-gray-light-600 text-xs font-medium">Taxes et frais compris</p>
      </div>

      {/* Conditions (en haut) */}
      {priceData?.conditions && (
        <SidebarCard title="Conditions du billet">
          <TicketConditions
            conditions={priceData.conditions}
            debugRule={SHOW_RULE_DEBUG ? priceData.conditionsRuleDebug : undefined}
          />
        </SidebarCard>
      )}

      {/* Journey cards */}
      {priceData?.journeys && priceData.journeys.length > 0 && (
        <div className="mt-2 flex flex-col gap-2">
          {priceData.journeys.map((journey, i) => {
            const firstSegment = journey.segments[0]
            const lastSegment = journey.segments[journey.segments.length - 1]
            if (!firstSegment || !lastSegment) return null

            const isMultiDestination = priceData.journeys.length > 2
            const journeyLabel = isMultiDestination
              ? `Vol ${i + 1}`
              : journey.type === 'segment'
                ? `Vol ${i + 1}`
                : JOURNEY_TYPE_LABELS[journey.type]

            const route = `${firstSegment.departure.airport} vers ${lastSegment.arrival.airport}`
            const stopsLabel =
              journey.stops === 0
                ? 'Direct'
                : `${journey.stops} escale${journey.stops > 1 ? 's' : ''}`

            return (
              <FlightSegmentCard
                key={i}
                airline={journey.carrier.name}
                airlineLogoUrl={journey.carrier.logoUrl}
                route={route}
                departureLabel={`${firstSegment.departure.date} à ${firstSegment.departure.time}`}
                arrivalLabel={`${lastSegment.arrival.date} à ${lastSegment.arrival.time}`}
                journeyTypeLabel={journeyLabel}
                stopsLabel={stopsLabel}
                durationLabel={formatDuration(journey.totalDuration, 'compact')}
              />
            )
          })}
        </div>
      )}

      {/* Contact info (checkout only) */}
      {contactInfo && (
        <SidebarCard title="Information du contact">
          <div className="text-gray-light-700 flex flex-col gap-2 text-sm font-medium">
            {contactInfo.phone && <p>{contactInfo.phone}</p>}
            <p>{contactInfo.email}</p>
          </div>
        </SidebarCard>
      )}

      {/* Passenger details */}
      {passengers.length > 0 && (
        <SidebarCard title="Détails des voyageurs">
          {passengers.map((passenger, index) => (
            <PassengerCard
              key={`passenger-${index}`}
              firstName={passenger.firstName}
              lastName={passenger.lastName}
              type={passenger.type}
              gender={passenger.gender}
              birthDate={passenger.birthDate}
            />
          ))}
        </SidebarCard>
      )}

      {/* Luggage */}
      {priceData?.luggage && (
        <SidebarCard title="Bagages inclus">
          <div className="flex flex-col gap-4 pl-4">
            <div className="flex items-start gap-5">
              <Luggage className="text-gray-light-600 size-6 shrink-0" />
              <div className="flex flex-col gap-2">
                <p className="text-gray-light-700 text-sm font-medium">
                  {priceData.luggage.checkedBags.included
                    ? `${priceData.luggage.checkedBags.quantity} bagage${priceData.luggage.checkedBags.quantity > 1 ? 's' : ''} soute`
                    : 'Aucun bagage en soute inclus'}
                </p>
                {priceData.luggage.checkedBags.included && (
                  <p className="text-gray-light-600 text-xs font-medium">
                    Poids maximum : {priceData.luggage.checkedBags.weightKgPerBag} kg chacun
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-start gap-5">
              <Briefcase className="text-gray-light-600 size-6 shrink-0" />
              <div className="flex flex-col gap-2">
                <p className="text-gray-light-700 text-sm font-medium">
                  {priceData.luggage.cabinBag.included
                    ? `${priceData.luggage.cabinBag.quantity ?? 1} bagage${(priceData.luggage.cabinBag.quantity ?? 1) > 1 ? 's' : ''} cabine`
                    : 'Pas de bagage cabine'}
                </p>
                {priceData.luggage.cabinBag.included && (
                  <p className="text-gray-light-600 text-xs font-medium">
                    Poids maximum : {priceData.luggage.cabinBag.weightKg} kg chacun
                  </p>
                )}
              </div>
            </div>
          </div>
        </SidebarCard>
      )}
    </div>
  )
}

// ── Sub-components ──

function SidebarCard({ children, title }: { children: ReactNode; title: string }) {
  return (
    <div className="border-gray-light-200 flex flex-col gap-4 rounded-sm border bg-white px-4 py-6">
      <p className="text-gray-light-900 text-sm font-semibold">{title}</p>
      {children}
    </div>
  )
}
